/**
 * Mobile Incident Report Page
 * Allows creating incident reports offline with auto-sync
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Save, AlertTriangle, Clock, MapPin, Camera } from 'lucide-react';
import { queueOfflineAction, ACTION_TYPES } from '@/lib/offline/actionQueue';
import { syncAllActions, isOnline } from '@/lib/offline/syncEngine';
import { SyncStatusBadge, OfflineIndicator, SYNC_STATUS } from '@/components/mobile/SyncStatusBadge';
import MobileAttachmentCapture from '@/components/mobile/MobileAttachmentCapture';
import { ATTACHMENT_ENTITY_TYPES } from '@/lib/offline/attachmentStorage';

const INCIDENT_TYPES = [
  { value: 'FALL', label: 'Fall' },
  { value: 'MEDICATION_ERROR', label: 'Medication Error' },
  { value: 'INJURY', label: 'Injury' },
  { value: 'BEHAVIORAL', label: 'Behavioral Incident' },
  { value: 'PROPERTY_DAMAGE', label: 'Property Damage' },
  { value: 'NEAR_MISS', label: 'Near Miss' },
  { value: 'ABUSE_ALLEGATION', label: 'Abuse Allegation' },
  { value: 'OTHER', label: 'Other' },
];

const SEVERITY_LEVELS = [
  { value: 'LOW', label: 'Low', color: 'bg-green-500/10 text-green-600 border-green-500/30' },
  { value: 'MEDIUM', label: 'Medium', color: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
  { value: 'HIGH', label: 'High', color: 'bg-orange-500/10 text-orange-600 border-orange-500/30' },
  { value: 'CRITICAL', label: 'Critical', color: 'bg-red-500/10 text-red-600 border-red-500/30' },
];

export default function MobileIncidentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('participantId');
  const shiftId = searchParams.get('shiftId');

  const [online, setOnline] = useState(isOnline());
  const [saving, setSaving] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [form, setForm] = useState({
    participantId: participantId || '',
    shiftId: shiftId || '',
    incidentType: '',
    severity: 'MEDIUM',
    description: '',
    location: '',
    immediateActions: '',
    witnesses: '',
  });

  // Online/offline listener
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch participants
  useEffect(() => {
    if (online) {
      api.get('/participants', { params: { pageSize: 100 } })
        .then(res => setParticipants(res.data.items || []))
        .catch(() => {});
    }
  }, [online]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const saveDraft = async () => {
    if (!form.incidentType) {
      toast.error('Please select incident type');
      return;
    }
    if (!form.description.trim()) {
      toast.error('Please enter incident description');
      return;
    }

    setSaving(true);
    setSyncStatus(SYNC_STATUS.PENDING);

    try {
      // Queue the action for offline sync
      await queueOfflineAction(ACTION_TYPES.INCIDENT_REPORT, {
        participantId: form.participantId,
        shiftId: form.shiftId,
        incidentType: form.incidentType,
        severity: form.severity,
        description: form.description,
        location: form.location,
        immediateActions: form.immediateActions,
        witnesses: form.witnesses ? form.witnesses.split(',').map(w => w.trim()).filter(Boolean) : [],
        timestamp: new Date().toISOString(),
        attachmentIds: attachments.map(a => a.id),
      });

      toast.success('Incident report saved locally', {
        description: online ? 'Syncing...' : 'Will sync when online',
      });

      // If online, try to sync immediately
      if (online) {
        setSyncStatus(SYNC_STATUS.SYNCING);
        try {
          const results = await syncAllActions();
          if (results.synced > 0) {
            setSyncStatus(SYNC_STATUS.SYNCED);
            toast.success('Report synced successfully');
            setTimeout(() => navigate(-1), 1000);
          } else if (results.failed > 0) {
            setSyncStatus(SYNC_STATUS.FAILED);
          }
        } catch (err) {
          setSyncStatus(SYNC_STATUS.FAILED);
        }
      } else {
        setTimeout(() => navigate(-1), 1500);
      }
    } catch (err) {
      toast.error('Failed to save report');
      setSyncStatus(SYNC_STATUS.FAILED);
    } finally {
      setSaving(false);
    }
  };

  const handleRetrySync = async () => {
    setSyncStatus(SYNC_STATUS.SYNCING);
    try {
      const results = await syncAllActions();
      if (results.synced > 0) {
        setSyncStatus(SYNC_STATUS.SYNCED);
        toast.success('Synced successfully');
      } else {
        setSyncStatus(SYNC_STATUS.FAILED);
      }
    } catch (err) {
      setSyncStatus(SYNC_STATUS.FAILED);
      toast.error('Sync failed');
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="mobile-incident-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-red-500 text-white px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-white hover:bg-red-600" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              <h1 className="text-lg font-bold">Incident Report</h1>
            </div>
            <p className="text-xs text-red-100">Report safety incidents immediately</p>
          </div>
        </div>
      </div>

      {/* Offline/Sync Status */}
      <div className="px-4 pt-4 space-y-2">
        <OfflineIndicator isOffline={!online} />
        {syncStatus && (
          <SyncStatusBadge 
            status={syncStatus} 
            onRetry={syncStatus === SYNC_STATUS.FAILED ? handleRetrySync : undefined}
          />
        )}
      </div>

      {/* Form */}
      <div className="p-4 space-y-4">
        {/* Incident Type & Severity */}
        <Card className="border-red-500/20">
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="incidentType">Incident Type *</Label>
              <Select 
                value={form.incidentType || undefined} 
                onValueChange={(v) => handleChange('incidentType', v)}
              >
                <SelectTrigger id="incidentType" data-testid="incident-type-select">
                  <SelectValue placeholder="Select incident type" />
                </SelectTrigger>
                <SelectContent>
                  {INCIDENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Severity Level *</Label>
              <div className="grid grid-cols-2 gap-2">
                {SEVERITY_LEVELS.map(({ value, label, color }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleChange('severity', value)}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      form.severity === value 
                        ? `${color} border-current` 
                        : 'bg-muted/50 border-transparent hover:bg-muted'
                    }`}
                    data-testid={`severity-${value.toLowerCase()}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Participant */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="participant">Participant Involved (Optional)</Label>
              <Select 
                value={form.participantId || undefined} 
                onValueChange={(v) => handleChange('participantId', v)}
              >
                <SelectTrigger id="participant" data-testid="participant-select">
                  <SelectValue placeholder="Select participant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None / General</SelectItem>
                  {participants.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.first_name} {p.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="location"
                  placeholder="Where did this occur?"
                  value={form.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="pl-10"
                  data-testid="location-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Description */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">What Happened? *</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Textarea
              id="description"
              placeholder="Describe the incident in detail..."
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={5}
              className="resize-none"
              data-testid="description-input"
            />
          </CardContent>
        </Card>

        {/* Immediate Actions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Immediate Actions Taken</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Textarea
              id="immediateActions"
              placeholder="What actions were taken immediately after the incident?"
              value={form.immediateActions}
              onChange={(e) => handleChange('immediateActions', e.target.value)}
              rows={3}
              className="resize-none"
              data-testid="actions-input"
            />
          </CardContent>
        </Card>

        {/* Witnesses */}
        <Card>
          <CardContent className="p-4">
            <div className="space-y-2">
              <Label htmlFor="witnesses">Witnesses (Optional)</Label>
              <Input
                id="witnesses"
                placeholder="Names of witnesses, separated by commas"
                value={form.witnesses}
                onChange={(e) => handleChange('witnesses', e.target.value)}
                data-testid="witnesses-input"
              />
              <p className="text-xs text-muted-foreground">
                Enter names separated by commas
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Attachments */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Incident Photos
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <MobileAttachmentCapture
              entityType={ATTACHMENT_ENTITY_TYPES.INCIDENT}
              entityId={null}
              maxAttachments={5}
              acceptedTypes="image/*"
              onAttachmentsChange={setAttachments}
              initialAttachments={attachments}
            />
          </CardContent>
        </Card>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 safe-area-inset-bottom">
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button 
            className="flex-1 bg-red-500 hover:bg-red-600"
            onClick={saveDraft}
            disabled={saving || !form.incidentType || !form.description.trim()}
            data-testid="save-incident-btn"
          >
            {saving ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 mr-2" />
                {online ? 'Submit Report' : 'Save Draft'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
